/**
 * Image Compression Utility for Sun-Spot
 * Uses browser-image-compression for client-side compression before upload
 */
import imageCompression from 'browser-image-compression';

// Compression options
const DEFAULT_OPTIONS = {
  maxSizeMB: 1,           // Max file size in MB
  maxWidthOrHeight: 1920, // Max dimension (maintains aspect ratio)
  useWebWorker: true,     // Use web worker for better performance
  fileType: 'image/jpeg', // Output format
  initialQuality: 0.8,    // JPEG quality (0-1)
};

// Thumbnail options (for previews)
const THUMBNAIL_OPTIONS = {
  maxSizeMB: 0.1,
  maxWidthOrHeight: 400,
  useWebWorker: true,
  fileType: 'image/jpeg',
  initialQuality: 0.7,
};

/**
 * Compress a single image file
 * @param {File} file - The image file to compress
 * @param {Object} options - Optional compression options override
 * @returns {Promise<{compressed: File, preview: string, originalSize: number, compressedSize: number}>}
 */
export async function compressImage(file, options = {}) {
  const compressionOptions = { ...DEFAULT_OPTIONS, ...options };
  
  try {
    const originalSize = file.size;
    
    // Compress the image
    const compressedFile = await imageCompression(file, compressionOptions);
    
    // Create a preview URL
    const preview = URL.createObjectURL(compressedFile);
    
    // Log compression stats
    const compressedSize = compressedFile.size;
    const ratio = ((1 - compressedSize / originalSize) * 100).toFixed(1);
    console.log(`Image compressed: ${(originalSize / 1024).toFixed(1)}KB → ${(compressedSize / 1024).toFixed(1)}KB (${ratio}% reduction)`);
    
    return {
      compressed: compressedFile,
      preview,
      originalSize,
      compressedSize,
      compressionRatio: ratio
    };
  } catch (error) {
    console.error('Image compression failed:', error);
    throw error;
  }
}

/**
 * Compress multiple images
 * @param {FileList|File[]} files - Array of image files
 * @param {Function} onProgress - Progress callback (index, total)
 * @returns {Promise<Array>}
 */
export async function compressImages(files, onProgress = null) {
  const results = [];
  const fileArray = Array.from(files);
  
  for (let i = 0; i < fileArray.length; i++) {
    const file = fileArray[i];
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      console.warn(`Skipping non-image file: ${file.name}`);
      continue;
    }
    
    try {
      const result = await compressImage(file);
      results.push({
        ...result,
        originalName: file.name,
        id: `${Date.now()}-${i}-${Math.random().toString(36).substr(2, 9)}`
      });
      
      if (onProgress) {
        onProgress(i + 1, fileArray.length);
      }
    } catch (error) {
      console.error(`Failed to compress ${file.name}:`, error);
    }
  }
  
  return results;
}

/**
 * Create a thumbnail from an image file
 * @param {File} file - The image file
 * @returns {Promise<string>} - Data URL of the thumbnail
 */
export async function createThumbnail(file) {
  try {
    const compressedFile = await imageCompression(file, THUMBNAIL_OPTIONS);
    return URL.createObjectURL(compressedFile);
  } catch (error) {
    console.error('Thumbnail creation failed:', error);
    // Fallback to original file preview
    return URL.createObjectURL(file);
  }
}

/**
 * Validate image file
 * @param {File} file - The file to validate
 * @returns {{valid: boolean, error?: string}}
 */
export function validateImage(file) {
  const MAX_SIZE_MB = 20; // Max 20MB before compression
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
  
  if (!file) {
    return { valid: false, error: 'Aucun fichier sélectionné' };
  }
  
  if (!ALLOWED_TYPES.includes(file.type)) {
    return { valid: false, error: 'Type de fichier non supporté. Utilisez JPG, PNG, WebP ou GIF.' };
  }
  
  if (file.size > MAX_SIZE_MB * 1024 * 1024) {
    return { valid: false, error: `Fichier trop volumineux. Maximum ${MAX_SIZE_MB}MB.` };
  }
  
  return { valid: true };
}

/**
 * Clean up preview URLs to prevent memory leaks
 * @param {string[]} urls - Array of object URLs to revoke
 */
export function cleanupPreviews(urls) {
  urls.forEach(url => {
    if (url && url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  });
}

export default {
  compressImage,
  compressImages,
  createThumbnail,
  validateImage,
  cleanupPreviews
};
